/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import "./view-surveys-form-response-list.scss";
import { getSurveySubmittedForms } from "../../../../api/surveys";
import { useLogin } from "../../../../common/hooks/useUser";
import { BreadcrumbComponent, MUITable, PaginationComponent } from "../../../../common/components";
import { breadcrumbLinkResponseList } from "../view-surveys-form-list.constant";
import LeadFilter from "./lead-filter/lead-filter";
import ShareIcon from "../../../../assets/icons/eye-light.svg";
import ViewSurveysForms from "../create-lead-capture-form/view-surveys-forms";
import { isValidPhoneNumber } from "../../../../utils/utils";

interface LeadCapturedFormLeadListProps {
  selectedLeadCaptureFormId?: string | number;
  loading?:any;
  setLoading?: (loading: boolean) => void;
  viewOpenForm?: any;
  setViewOpenForm?: any;
  setAlertMessage?: (message: string) => void;
  setAlertType?: (type: "error" | "success" | "info" | "warning") => void;
  setAlertOpen?: (open: boolean) => void;
  setResponseViewOpen?: any;
  setIsShowCreateLeadSection?: any;
}

interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}

const SurveysFormResponseList: React.FC<LeadCapturedFormLeadListProps> = (props: any) => {
  const { selectedLeadCaptureFormId,loading, setLoading, setViewOpenForm, setAlertMessage, setAlertType, setAlertOpen, setResponseViewOpen, setIsShowCreateLeadSection } = props;
  const [crumbMappingOption, setCrumbMappingOption] =
    useState<any>([]);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0); // Set an initial value for testing
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [data, setData] = useState<any[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [createLeadCaptureInitialValues, setCreateLeadCaptureInitialValues] = useState<any>({});
  const isSearchTriggered = useRef(false);

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1)
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };

  const userDetails = useLogin();

  const leadOwner =
    userDetails?.userDetails?.firstName +
    " " +
    userDetails?.userDetails?.lastName;

  const fetchLeads = async (page = 1, search: string = "", filters: any = {}) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.search = search;
      }
      const response = await getSurveySubmittedForms(params, "Lead Capture", selectedLeadCaptureFormId);
      setData(response?.data?.data?.data || []);
      setTotalRecords(response.data.data.totalCount || 0);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(error?.response?.data?.message || "Failed to fetch campaigns");
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  
  useEffect(() => {
   setCrumbMappingOption( breadcrumbLinkResponseList(setViewOpenForm, setCrumbMappingOption));
    if (!viewOpen) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchLeads(pageToUse, searchText);
      isSearchTriggered.current = false;
    }
  }, [currentPage, searchText, sortOption, viewOpen]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const headers = [
    { key: "firstNames", label: "First Name", isSortable: true },
    { key: "lastName", label: "Last Name", isSortable: true },
    { key: "email", label: "Email", isSortable: true },
    { key: "phone", label: "Mobile Number", isSortable: true },
    { key: "age", label: "Age", isSortable: true },
    { key: "preview", label: "Preview", isSortable: false },
  ];
  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      email: "primary_email_address",
      phone: "primary_phone_number",
      leadSource: "lead_source",
      leadOwner: "lead_owner",
      leadStatus: "lead_status",
    };

    return mapping[columnName] || columnName;
  };

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        const firstName = item.first_name?.trim();
        const lastName = item.last_name?.trim();
        const fullName =
          firstName && lastName
            ? `${firstName} ${lastName}`
            : firstName || lastName || "-";

        const leadName = fullName;

       
        return {
          id: item.id,
          // leadName: leadName,
          firstNames: item.first_name || "-",
          lastName: item.last_name || "-",
          email: item.primary_email_address || "-",
          phone: isValidPhoneNumber(item.primary_phone_number)
            ? item.primary_phone_number
            : "-",
          age: item.age || "-",
          allData: item,
        };
      });
    }
    return [];
  }, [data, leadOwner]);

  const handleViewClick = async (surveyData: any) => {
    setCreateLeadCaptureInitialValues(surveyData?.allData?.allData);
    setViewOpen(true);
  };

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading response...";
    }
    if (searchText) {
      return "No response found matching your search criteria";
    }
    return "No response have been captured from this form yet";
  }, [loading, searchText]);

  return (
    <>
    {viewOpen ?  (
          <ViewSurveysForms
            setLoading={setLoading}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
            setAlertOpen={setAlertOpen}
            createLeadCaptureInitialValues={createLeadCaptureInitialValues}
            setCreateLeadCaptureInitialValues={setCreateLeadCaptureInitialValues}
            setIsShowCreateLeadSection={setIsShowCreateLeadSection}
            selectedLeadCaptureFormId={selectedLeadCaptureFormId}
            setViewOpenForm={setViewOpenForm}
            setViewOpen={setViewOpen}
            setResponseViewOpen={setResponseViewOpen}
          />
        ): 
      <div
        className="lead-captured-form-leadlist-wrapper"
      >
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent
            breadcrumbLink={crumbMappingOption}
            showBackArrow={true}
            handleBackArrowClick={() => {
              setViewOpenForm(false);
            }} />
        </div>

        <div style={{ display: !viewOpen ? "block" : "none" }}>
          <LeadFilter
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            // selectedLeadsFilter={appliedFilterCount}
            onSearch={handleSearch}
            searchRef={searchRef}
            isResetSearch={isResetSearch}
            resetSearch={resetSearch}
            handleClickCreateNewForm={() => {
              setResponseViewOpen(false) 
              setIsShowCreateLeadSection(true);
            }}
            fetchLeads={() => {
              setIsResetSearch(!isResetSearch);
              setSearchText("");
              fetchLeads(currentPage);
            }}
          />

          <div className="lead-captured-form-lead-list-table-filter">
            <div
              className="lead-captured-form-lead-list-table"
              style={isOpenFilter ? { width: "80%" } : {}}
            >
              <MUITable
                headers={headers}
                data={formattedData.map((item) => ({
                  key: item.id,
                  firstNames: item.firstNames,
                  lastName: item.lastName,
                  email: item.email,
                  phone: item.phone,
                  age: item.age,
                  preview: <img src={ShareIcon} alt="Preview" className="preview-icon"/>,
                  allData: item,
                  //leadCreatedBy: item.leadCreatedBy,
                }))}
                onSort={handleSort}
                noResultsTxt={noResultText}
                onRowClick={(rowData: any) => handleViewClick(rowData)}
              />
            </div>
        {totalRecords && totalRecords > 0 ? (
          <div className="leads-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
          </div>
          ) : (
            <></>
          )}                    
          </div>
        </div>
      </div>}
    </>
  );
};

export default SurveysFormResponseList;